import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// import { useSelector } from 'react-redux'; // Commented out as it's not used
import Sidebar from './Sidebar';
import { useMobileSidebar } from '../../hooks/useMobileSidebar';
import { supportedLanguages } from '../../i18n';

const AdminLayout = ({ children }) => {
  // const { admin } = useSelector(state => state.auth); // Commented out as it's not used
  const location = useLocation();
  const { isSidebarOpen, isMobile, toggleSidebar } = useMobileSidebar();
  const { t, i18n } = useTranslation();
  const [lang, setLang] = useState(i18n.language || 'zh-TW');
  
  // Extract the active tab from the current route
  const activeTab = location.pathname.slice(1) || 'dashboard';

  const pageTitle = t(`navigation.${activeTab}`, {
    defaultValue: getDefaultTitle(activeTab)
  });
  const pageDescription = t(`navigationDesc.${activeTab}`, {
    defaultValue: getPageDescription(activeTab)
  });

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const handleLangChange = (event) => {
    const newLang = event.target.value;
    setLang(newLang);
    i18n.changeLanguage(newLang);
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem('ccbc-lang', newLang);
      }
    } catch (e) {
      // Ignore storage errors
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <Sidebar isOpen={isSidebarOpen} onToggle={toggleSidebar} />
      
      {/* Main Content */}
      <div className={`
        flex-1 flex flex-col overflow-hidden transition-all duration-300 ease-in-out
        ${isMobile ? 'w-full' : ''}
      `}>
        {/* Top Header */}
        <header className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {/* Mobile Hamburger Menu */}
              {isMobile && (
                <button
                  onClick={toggleSidebar}
                  className="p-2 mr-4 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors md:hidden"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              )}
              
              <div>
                <h2 className="text-2xl font-semibold text-gray-800 capitalize">
                  {pageTitle}
                </h2>
                <p className="text-gray-600 text-sm mt-1">
                  {pageDescription}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Language Switcher - Disabled */}
              <label className="flex items-center space-x-2 text-sm text-gray-600">
                <span>{t('language.label')}:</span>
                <select
                  className="border border-gray-300 rounded-md px-2 py-1 bg-gray-100 text-gray-700 cursor-not-allowed opacity-60"
                  value={lang}
                  onChange={handleLangChange}
                  disabled
                >
                  {supportedLanguages.map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              {/* Notifications */}
              <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </button>
              
              {/* User Status */}
              <div className="flex items-center">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                </div>
                <span className="text-sm text-gray-600">{t('status.online')}</span>
              </div>
            </div>
          </div>
        </header>
        
        {/* Page Content */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-full mx-auto p-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

// Helper function to get page descriptions
const getPageDescription = (activeTab) => {
  const descriptions = {
    dashboard: 'Overview of your CCBC platform statistics and activities',
    admins: 'Manage CCBC platform administrators and their permissions',
    posts: 'Create, edit, and manage platform posts and articles',
    events: 'Manage events and their sections/classes with datetime scheduling',
    attendance: 'View and manage attendance records from check-ins',
    'attendance-member': 'List all attendance records',
    check_in: 'Check in attendance by scanning QR codes for event sections',
    continuous_checkin: 'Continuous QR scanner - scan multiple QR codes one by one without stopping',
    joomla: 'View and monitor articles from your Joomla CMS',
    'access-control': 'Configure user permissions and access levels',
    'room-booking': 'Manage room bookings and availability',
    pos: 'Sell gifts, books, and courses',
    settings: 'System settings and dangerous maintenance operations'
  };
  
  return descriptions[activeTab] || 'Manage your CCBC platform';
};

const getDefaultTitle = (activeTab) => {
  const titles = {
    dashboard: 'Dashboard',
    admins: 'Admin Management',
    posts: 'Posts',
    events: 'Events',
    attendance: 'Attendance Records',
    'attendance-member': 'All Attendance Records',
    check_in: 'Check In (QR Scan)',
    continuous_checkin: 'Continuous Check-in',
    joomla: 'Joomla Articles',
    'access-control': 'Access Control',
    members: 'Member Management',
    organization: 'Organization Structure',
    'tithe-receipts': 'Tithe Receipt Management',
    'room-booking': 'Room Booking',
    pos: 'Mini POS',
    settings: 'System Settings'
  };

  return titles[activeTab] || activeTab;
};

export default AdminLayout;
